import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { DateRange } from '@angular/material/datepicker';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { LocalizedRange } from './localizedRange';

import * as moment from 'moment-timezone';

import { getOffsets, getTimes, TimeZone } from './zones';

@Component({
  selector: 'atm-timezone-select',
  templateUrl: './timezone-select.component.html',
  styleUrls: ['./timezone-select.component.scss'],
})
export class TimezoneSelectComponent {
  timeZones = getOffsets;
  uniqueTimezones = uniqBy(getOffsets, (x: TimeZone) => x.offset);
  hourIntervals = getTimes;

  start?: moment.Moment;
  end?: moment.Moment;
  startBoundaryInclusive = false;
  endBoundaryInclusive = false;

  startTime: number = 0;
  endTime: number = 0;

  selectedTimeZone: TimeZone = getOffsets.find((z) => z.name === 'GMT')!;

  constructor(
    public dialogRef: MatDialogRef<TimezoneSelectComponent>,
    @Inject(MAT_DIALOG_DATA) public data: LocalizedRange
  ) {
    this.start = moment.parseZone(data.min).utcOffset(0);
    this.end = moment.parseZone(data.max).utcOffset(0);
    this.selectedRangeValue = new DateRange<Date>(
      this.start.toDate() ?? null,
      this.end.toDate() ?? null
    );

    this.startBoundaryInclusive = data.inclusive.min;
    this.endBoundaryInclusive = data.inclusive.max;

    // get the time in minutes
    this.startTime = this.start
      ? this.start.minutes() + this.start.hours() * 60
      : 0;
    this.endTime = this.end ? this.end.minutes() + this.end.hours() * 60 : 0;

    const offsetSource = this.start || this.end;
    if (offsetSource) {
      const parsedOffset = moment.parseZone(offsetSource).utcOffset();

      this.selectedTimeZone = getOffsets.find(
        (z) => z.offset === parsedOffset
      )!;
    }
  }

  @Input() selectedRangeValue: DateRange<Date> | undefined;
  @Output() selectedRangeValueChange = new EventEmitter<DateRange<Date>>();

  selectedChange(m: any) {
    if (!this.selectedRangeValue?.start || this.selectedRangeValue?.end) {
      this.selectedRangeValue = new DateRange<Date>(m, null);
    } else {
      const start = this.selectedRangeValue.start;
      const end = m;
      if (end < start) {
        this.selectedRangeValue = new DateRange<Date>(end, start);
      } else {
        this.selectedRangeValue = new DateRange<Date>(start, end);
      }
    }
    this.selectedRangeValueChange.emit(this.selectedRangeValue);

    this.start = moment(this.selectedRangeValue.start) ?? undefined;
    this.end = moment(this.selectedRangeValue.end) ?? undefined;
  }

  close() {
    this.dialogRef.close();
  }

  getDateRange() {
    const min =
      this.start &&
      moment
        .parseZone(this.start)
        .set({ hour: 0, minute: 0 })
        .add(this.startTime, 'minutes')
        .utcOffset(0, true)
        .toDate();
    const max =
      this.end &&
      moment
        .parseZone(this.end)
        .set({ hour: 0, minute: 0 })
        .add(this.endTime, 'minutes')
        .utcOffset(0, true)
        .toDate();

    const lr = new LocalizedRange({
      min,
      max,
      inclusive: {
        min: this.startBoundaryInclusive,
        max: this.endBoundaryInclusive,
      },
      offset: this.selectedTimeZone?.offset ?? 0,
    });

    return lr;
  }
}

function uniqBy(a: any, key: any) {
  var seen: any = {};
  return a.filter(function (item: any) {
    var k = key(item);
    return seen.hasOwnProperty(k) ? false : (seen[k] = true);
  });
}
